import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import "../../assets/css/product-studio-edit.css";
import "../../assets/css/marketing-meta-ads.css";
import NotificationComponent from "../../components/Alert/alert";
import {
  useDeleteMetaProductSetMutation,
  useFetchMetaProductSetQuery,
  useIncludeMetaProductSetInCatalogMutation,
  useReplaceMetaProductSetProductsMutation,
  useUpdateMetaProductSetMutation,
} from "../../api/marketingApi";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslation } from "../../hooks/useTranslation";
import { Btn, ConfirmDialog, Icon, Menu, Notice, Spinner, cx } from "../catalogProductStudio/edit/StudioUi";
import useUnsavedLinkGuard from "../catalogProductStudio/edit/useUnsavedLinkGuard";
import { FeedStatus, ProductTile, TileSkeletons } from "./MetaAdsUi";
import ProductPickerToolbar from "./ProductPickerToolbar";
import ProductSetDetailsDrawer from "./ProductSetDetailsDrawer";
import MetaSetupPanel from "./MetaSetupPanel";
import usePickerProducts, { useDebouncedValue } from "./usePickerProducts";
import {
  DEFAULT_SORT,
  EMPTY_FILTERS,
  FEED_STATE_META,
  META_ADS_PATH,
  REACH_ISSUE_KEYS,
  countActiveFilters,
  diffSelection,
  fill,
  formatExactTime,
  formatRelativeTime,
  getApiErrorCode,
  getApiStatus,
  getBulkSelectionState,
  getFeedStateKey,
  humanizeMetaAdsError,
  matchesProductSearch,
  orderSelectedForView,
  plural,
  rangeBetween,
  summarizeReachIssues,
} from "./metaAdsModel";

const LAYOUT_STORAGE_KEY = "bw.metaAds.layout";

const readStoredLayout = () => {
  try {
    return window.localStorage.getItem(LAYOUT_STORAGE_KEY) === "list" ? "list" : "grid";
  } catch (_error) {
    return "grid";
  }
};

/** Tri-state "select all" checkbox (indeterminate needs a DOM property). */
const BulkCheckbox = ({ state, onChange, label, disabled }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "some";
  }, [state]);
  return (
    <label className={cx("mka-bulk", disabled && "is-disabled")}>
      <input
        ref={ref}
        type="checkbox"
        className="mka-check__input"
        checked={state === "all"}
        disabled={disabled}
        onChange={onChange}
      />
      <span className={cx("mka-check", state === "some" && "is-mixed")} aria-hidden="true">
        <Icon name={state === "some" ? "minus" : "check"} size={13} strokeWidth={3} />
      </span>
      <span className="mka-bulk__label">{label}</span>
    </label>
  );
};

const ReachAttention = ({ summary, tt, onShow, onInclude, isIncluding, includeDisabled }) => {
  if (!summary.total) return null;
  return (
    <Notice
      tone="warning"
      className="mka-attention"
      title={plural(tt, "attentionTitle", summary.total)}
      action={
        <div className="mka-attention__actions">
          {summary.fixableInCatalog > 0 ? (
            <Btn
              variant="secondary"
              size="sm"
              icon="meta"
              onClick={onInclude}
              disabled={isIncluding || includeDisabled}
              title={includeDisabled ? tt("includeSaveFirst") : undefined}
            >
              {isIncluding ? tt("including") : plural(tt, "includeInCatalog", summary.fixableInCatalog)}
            </Btn>
          ) : null}
          <Btn variant="ghost" size="sm" onClick={onShow}>
            {tt("attentionShow")}
          </Btn>
        </div>
      }
    >
      <ul className="mka-attention__list">
        {summary.groups.map(({ issue, count }) => (
          <li key={issue}>
            {plural(tt, `${REACH_ISSUE_KEYS[issue]}Count`, count)}
          </li>
        ))}
      </ul>
    </Notice>
  );
};

const MetaProductSetManage = () => {
  const t = useTranslation();
  const tt = useCallback((key) => t(`metaAds.${key}`), [t]);
  const { language } = useLanguage();
  const { setId } = useParams();
  const productSetId = Number(setId);
  const navigate = useNavigate();
  const location = useLocation();
  const notificationRef = useRef(null);
  const notify = useCallback((message, type = "success") => {
    notificationRef.current?.showNotification(message, type);
  }, []);

  const detailQuery = useFetchMetaProductSetQuery(productSetId, {
    skip: !Number.isInteger(productSetId) || productSetId <= 0,
    refetchOnMountOrArgChange: true,
  });
  const [replaceProducts, { isLoading: isSaving }] = useReplaceMetaProductSetProductsMutation();
  const [updateProductSet] = useUpdateMetaProductSetMutation();
  const [deleteProductSet, { isLoading: isDeleting }] = useDeleteMetaProductSetMutation();
  const [includeInCatalog, { isLoading: isIncluding }] = useIncludeMetaProductSetInCatalogMutation();

  // ── Saved state vs. working selection ─────────────────────────────────────
  const [productSet, setProductSet] = useState(null);
  const [baseline, setBaseline] = useState(null); // { ids: Set, version }
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const productCacheRef = useRef(new Map());
  const [cacheVersion, setCacheVersion] = useState(0);
  const rememberProducts = useCallback((products) => {
    let changed = false;
    (products || []).forEach((product) => {
      if (productCacheRef.current.get(product.key) !== product) {
        productCacheRef.current.set(product.key, product);
        changed = true;
      }
    });
    if (changed) setCacheVersion((version) => version + 1);
  }, []);

  const diff = useMemo(
    () => diffSelection(baseline?.ids || new Set(), selectedIds),
    [baseline, selectedIds]
  );
  const isDirty = Boolean(baseline) && diff.isDirty;

  /** Take the server's version as the new saved state. */
  const adoptSaved = useCallback(
    (detail, { keepSelection = false } = {}) => {
      if (!detail) return;
      rememberProducts(detail.selections);
      setProductSet(detail);
      const ids = new Set(detail.selectionKeys || []);
      setBaseline({ ids, version: detail.productsUpdatedAt });
      if (!keepSelection) setSelectedIds(new Set(ids));
    },
    [rememberProducts]
  );

  // A refetch (rename, retry) never throws away a selection in progress. If
  // someone else changed the set meanwhile, the save reports the conflict.
  const isDirtyRef = useRef(false);
  isDirtyRef.current = isDirty;
  useEffect(() => {
    const detail = detailQuery.data;
    if (!detail) return;
    if (!isDirtyRef.current) {
      adoptSaved(detail);
    } else {
      rememberProducts(detail.selections);
      setProductSet((current) => ({ ...detail, selectionKeys: current?.selectionKeys || detail.selectionKeys }));
    }
  }, [detailQuery.data, adoptSaved, rememberProducts]);

  // ── View, filters, layout ─────────────────────────────────────────────────
  const justCreated = Boolean(location.state?.justCreated);
  const [view, setView] = useState(null); // "selected" | "all"
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [layout, setLayout] = useState(readStoredLayout);
  const [selectedViewIds, setSelectedViewIds] = useState([]);
  const [selectedViewReachOnly, setSelectedViewReachOnly] = useState(false);
  const lastToggledRef = useRef(null);

  useEffect(() => {
    if (view || !productSet) return;
    setView(productSet.selectionKeys.length ? "selected" : "all");
  }, [productSet, view]);

  useEffect(() => {
    if (justCreated && productSet) {
      notify(tt("toastCreated"));
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [justCreated, productSet]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeLayout = (next) => {
    setLayout(next);
    try {
      window.localStorage.setItem(LAYOUT_STORAGE_KEY, next);
    } catch (_error) {
      // Private mode: the choice simply is not remembered.
    }
  };

  const openSelectedView = useCallback(
    ({ reachOnly = false } = {}) => {
      // A snapshot, so unticking a product here does not make it vanish
      // under the cursor — it stays, unticked, until the view is reopened.
      setSelectedViewIds(orderSelectedForView(selectedIds, baseline?.ids));
      setSelectedViewReachOnly(reachOnly);
      setView("selected");
    },
    [baseline, selectedIds]
  );

  useEffect(() => {
    if (view === "selected" && productSet && !selectedViewIds.length && selectedIds.size) {
      setSelectedViewIds(orderSelectedForView(selectedIds, baseline?.ids));
    }
  }, [view, productSet]); // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedSearch = useDebouncedValue(filters.search, 250);
  const serverFilters = useMemo(() => ({ ...filters, search: debouncedSearch }), [filters, debouncedSearch]);
  const picker = usePickerProducts(serverFilters, sort, { skip: view !== "all" });
  useEffect(() => rememberProducts(picker.items), [picker.items, rememberProducts]);

  const selectedViewProducts = useMemo(() => {
    if (view !== "selected") return [];
    return selectedViewIds
      .map((id) => productCacheRef.current.get(id))
      .filter(Boolean)
      .filter((product) => matchesProductSearch(product, filters.search))
      .filter((product) => !selectedViewReachOnly || !product.metaReach?.reachesMeta);
    // The cache is a ref; `cacheVersion` ticks whenever it gains fresher cards.
  }, [view, selectedViewIds, filters.search, selectedViewReachOnly, cacheVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleProducts = view === "selected" ? selectedViewProducts : picker.items;
  const visibleIds = useMemo(() => visibleProducts.map((product) => product.key), [visibleProducts]);
  const bulkIds = view === "selected" ? visibleIds : picker.matchingKeys;
  const bulkState = getBulkSelectionState(bulkIds, selectedIds);

  // ── Selection actions ─────────────────────────────────────────────────────
  const toggleProduct = useCallback(
    (id, withShift) => {
      setSelectedIds((current) => {
        const next = new Set(current);
        const shouldSelect = !current.has(id);
        const ids =
          withShift && lastToggledRef.current != null
            ? rangeBetween(visibleIds, lastToggledRef.current, id)
            : [id];
        ids.forEach((entry) => (shouldSelect ? next.add(entry) : next.delete(entry)));
        return next;
      });
      lastToggledRef.current = id;
    },
    [visibleIds]
  );

  const toggleBulk = () => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (bulkState === "all") bulkIds.forEach((id) => next.delete(id));
      else bulkIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const discardChanges = () => {
    if (!baseline) return;
    setSelectedIds(new Set(baseline.ids));
    if (view === "selected") setSelectedViewIds([...baseline.ids]);
  };

  // Tabs follow the WAI-ARIA pattern: arrow keys move between the two views.
  const handleTabKeyDown = (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const next = view === "all" ? "selected" : "all";
    if (next === "selected") openSelectedView();
    else setView("all");
    document.getElementById(`mka-tab-${next}`)?.focus();
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const [conflictOpen, setConflictOpen] = useState(false);
  const [removedUnknown, setRemovedUnknown] = useState(0);

  const save = useCallback(
    async ({ force = false } = {}) => {
      if (!baseline || !isDirty || isSaving) return;
      const summary = { added: diff.added.length, removed: diff.removed.length };
      try {
        const result = await replaceProducts({
          id: productSetId,
          selectionKeys: [...selectedIds],
          expectedProductsUpdatedAt: force ? undefined : baseline.version,
        }).unwrap();
        adoptSaved(result);
        setConflictOpen(false);
        setRemovedUnknown(0);
        notify(
          fill(tt("toastSaved"), {
            changes: [
              summary.added ? plural(tt, "addedCount", summary.added) : null,
              summary.removed ? plural(tt, "removedCount", summary.removed) : null,
            ]
              .filter(Boolean)
              .join(" · "),
          })
        );
      } catch (error) {
        const code = getApiErrorCode(error);
        if (code === "PRODUCT_SET_CHANGED") {
          setConflictOpen(true);
          return;
        }
        if (code === "UNKNOWN_PRODUCTS") {
          const unknown = new Set(error?.data?.details?.selectionKeys || []);
          setSelectedIds((current) => new Set([...current].filter((id) => !unknown.has(id))));
          setRemovedUnknown(unknown.size);
          return;
        }
        notify(
          `${humanizeMetaAdsError(error, tt, "errSave")} ${getApiStatus(error) === 403 ? "" : tt("errSaveKept")}`.trim(),
          "danger"
        );
      }
    },
    [adoptSaved, baseline, diff, isDirty, isSaving, notify, productSetId, replaceProducts, selectedIds, tt]
  );

  const reloadTheirs = async () => {
    setConflictOpen(false);
    const result = await detailQuery.refetch();
    if (result?.data) {
      adoptSaved(result.data);
      if (view === "selected") setSelectedViewIds([...result.data.selectionKeys]);
    }
    notify(tt("toastReloaded"), "info");
  };

  // ⌘S / Ctrl+S saves, like everywhere else on a desktop.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [save]);

  // ── Leaving with unsaved work ─────────────────────────────────────────────
  useEffect(() => {
    if (!isDirty) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);
  useUnsavedLinkGuard(isDirty, tt("leaveConfirm"));

  // ── Details / delete / catalog fix ────────────────────────────────────────
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const handleSaveDetails = async (values) => {
    const result = await updateProductSet({ id: productSetId, ...values }).unwrap();
    setProductSet((current) => ({ ...current, name: result.name, description: result.description }));
    setIsDetailsOpen(false);
    notify(tt("toastUpdated"));
  };

  const handleDelete = async () => {
    try {
      await deleteProductSet(productSetId).unwrap();
      navigate(META_ADS_PATH, { state: { toast: fill(tt("toastDeleted"), { name: productSet?.name || "" }) } });
    } catch (error) {
      notify(humanizeMetaAdsError(error, tt, "errDelete"), "danger");
    }
  };

  const handleIncludeInCatalog = async () => {
    try {
      const result = await includeInCatalog(productSetId).unwrap();
      adoptSaved(result.productSet, { keepSelection: true });
      notify(plural(tt, "toastIncluded", result.updatedCount));
    } catch (error) {
      notify(humanizeMetaAdsError(error, tt, "errInclude"), "danger");
    }
  };

  const savedProducts = useMemo(
    () => (productSet?.selectionKeys || []).map((key) => productCacheRef.current.get(key)).filter(Boolean),
    [productSet, cacheVersion] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const reachSummary = useMemo(() => summarizeReachIssues(savedProducts), [savedProducts]);

  // ── Infinite scroll ───────────────────────────────────────────────────────
  const sentinelRef = useRef(null);
  const { hasMore, loadMore, isFetchingMore } = picker;
  useEffect(() => {
    if (view !== "all" || !hasMore || typeof IntersectionObserver === "undefined") return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => entries.some((entry) => entry.isIntersecting) && loadMore(),
      { rootMargin: "600px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [view, hasMore, loadMore, isFetchingMore]);

  // ── Render ────────────────────────────────────────────────────────────────
  const isInvalidId = !Number.isInteger(productSetId) || productSetId <= 0;
  if (isInvalidId || (detailQuery.isError && !productSet)) {
    const status = isInvalidId ? 404 : getApiStatus(detailQuery.error);
    return (
      <div className="content pse mka">
        <div className="mka-topline">
          <Link to={META_ADS_PATH} className="pse-back">
            <Icon name="arrowLeft" size={16} />
            <span>{tt("backToSets")}</span>
          </Link>
        </div>
        <Notice
          tone={status === 404 || status === 403 ? "warning" : "danger"}
          icon={status === 403 ? "lock" : undefined}
          title={status === 404 ? tt("notFoundTitle") : status === 403 ? tt("forbiddenTitle") : tt("loadSetErrorTitle")}
          action={
            status === 404 || status === 403 ? null : (
              <Btn variant="secondary" size="sm" icon="refresh" onClick={() => detailQuery.refetch()}>
                {tt("retry")}
              </Btn>
            )
          }
        >
          {status === 404 ? tt("notFoundCopy") : status === 403 ? tt("forbiddenCopy") : tt("loadSetErrorCopy")}
        </Notice>
      </div>
    );
  }

  if (!productSet) {
    return (
      <div className="content pse mka" aria-busy="true">
        <div className="mka-topline">
          <span className="mka-skel mka-skel--back" />
        </div>
        <div className="mka-detail-head mka-detail-head--skeleton">
          <span className="mka-skel mka-skel--title" />
          <span className="mka-skel mka-skel--line mka-skel--short" />
        </div>
        <TileSkeletons count={10} layout={layout} />
        <span className="pse-visually-hidden">
          <Spinner label={tt("loadingSet")} />
        </span>
      </div>
    );
  }

  const stateKey = getFeedStateKey(productSet, isDirty);
  const stateMeta = FEED_STATE_META[stateKey];
  const stateHint = tt(stateMeta.hintKey);
  const selectedCount = selectedIds.size;
  const searchActive = Boolean(filters.search.trim());
  const filtersActive = countActiveFilters(filters) > 0;

  let results;
  if (view === "all" && picker.isError && !picker.items.length) {
    results = (
      <Notice
        tone="danger"
        title={tt("loadProductsErrorTitle")}
        action={
          <Btn variant="secondary" size="sm" icon="refresh" onClick={() => picker.refetch()}>
            {tt("retry")}
          </Btn>
        }
      >
        {humanizeMetaAdsError(picker.error, tt, "loadProductsErrorCopy")}
      </Notice>
    );
  } else if (view === "all" && picker.isInitialLoading) {
    results = <TileSkeletons count={12} layout={layout} />;
  } else if (!visibleProducts.length) {
    if (view === "selected" && !selectedViewIds.length) {
      results = (
        <div className="mka-empty">
          <span className="mka-empty__icon" aria-hidden="true">
            <Icon name="plus" size={22} />
          </span>
          <strong className="mka-empty__title">{tt("setEmptyTitle")}</strong>
          <p className="mka-empty__copy">{tt("setEmptyCopy")}</p>
          <Btn variant="primary" icon="plus" onClick={() => setView("all")}>
            {tt("addProducts")}
          </Btn>
        </div>
      );
    } else {
      results = (
        <div className="mka-empty">
          <span className="mka-empty__icon" aria-hidden="true">
            <Icon name="search" size={22} />
          </span>
          <strong className="mka-empty__title">
            {searchActive ? fill(tt("noResultsForSearch"), { search: filters.search.trim() }) : tt("noResultsTitle")}
          </strong>
          <p className="mka-empty__copy">{tt("noResultsCopy")}</p>
          <div className="mka-empty__actions">
            {searchActive ? (
              <Btn variant="secondary" onClick={() => setFilters((current) => ({ ...current, search: "" }))}>
                {tt("clearSearch")}
              </Btn>
            ) : null}
            {filtersActive || selectedViewReachOnly ? (
              <Btn
                variant="secondary"
                onClick={() => {
                  setFilters((current) => ({ ...EMPTY_FILTERS, search: current.search }));
                  setSelectedViewReachOnly(false);
                }}
              >
                {tt("clearFilters")}
              </Btn>
            ) : null}
          </div>
        </div>
      );
    }
  } else {
    results = (
      <>
        <ul
          className={cx("mka-results", `mka-results--${layout}`, picker.isRefreshing && view === "all" && "is-refreshing")}
          aria-label={tt(view === "selected" ? "resultsSelectedAria" : "resultsAllAria")}
        >
          {visibleProducts.map((product) => (
            <ProductTile
              key={product.key}
              product={product}
              layout={layout}
              selected={selectedIds.has(product.key)}
              onToggle={toggleProduct}
              tt={tt}
            />
          ))}
        </ul>
        {view === "all" ? (
          <div className="mka-more" ref={sentinelRef}>
            <span className="mka-muted">
              {fill(tt("showingOf"), { shown: picker.items.length, total: picker.total })}
            </span>
            {picker.hasMore ? (
              <Btn variant="secondary" onClick={picker.loadMore} disabled={picker.isFetchingMore}>
                {picker.isFetchingMore ? tt("loadingMore") : tt("loadMore")}
              </Btn>
            ) : null}
          </div>
        ) : null}
      </>
    );
  }

  const resultCount = view === "selected" ? visibleProducts.length : picker.total;

  return (
    <div className={cx("content pse mka", isDirty && "has-savebar")}>
      <NotificationComponent ref={notificationRef} />

      <div className="mka-topline">
        <Link to={META_ADS_PATH} className="pse-back">
          <Icon name="arrowLeft" size={16} />
          <span>{tt("backToSets")}</span>
        </Link>
      </div>

      <header className="mka-detail-head">
        <div className="mka-detail-head__text">
          <p className="mka-eyebrow">
            <span>{tt("eyebrowMarketing")}</span>
            <Icon name="chevronRight" size={12} />
            <span>{tt("eyebrowMetaAds")}</span>
            <Icon name="chevronRight" size={12} />
            <span>{tt("eyebrowProductSet")}</span>
          </p>
          <h1 className="mka-detail-head__title">{productSet.name}</h1>
          {productSet.description ? <p className="mka-detail-head__description">{productSet.description}</p> : null}
          <p className="mka-detail-head__meta">
            <FeedStatus stateKey={stateKey} tt={tt} title={stateHint} />
            <span>{plural(tt, "selectionCount", productSet.selectionCount)}</span>
            <span>{plural(tt, "variantCount", productSet.variantCount)}</span>
            <span title={formatExactTime(productSet.productsUpdatedAt, language)}>
              {fill(tt("updatedAgo"), {
                time: formatRelativeTime(productSet.productsUpdatedAt, language, Date.now(), tt("justNow")),
              })}
            </span>
          </p>
          <p className="mka-detail-head__hint">{stateHint}</p>
        </div>
        <div className="mka-detail-head__actions">
          <Menu
            label={tt("moreActions")}
            items={[
              { label: tt("menuEditDetails"), icon: "edit", onClick: () => setIsDetailsOpen(true) },
              { label: tt("menuDelete"), icon: "trash", tone: "danger", onClick: () => setIsDeleteOpen(true) },
            ]}
          />
          <Btn
            variant={isDirty ? "primary" : "secondary"}
            icon={isDirty ? null : "check"}
            onClick={() => save()}
            disabled={!isDirty || isSaving}
            aria-keyshortcuts="Meta+S Control+S"
          >
            {isSaving ? tt("saving") : isDirty ? plural(tt, "saveCount", diff.count) : tt("allSaved")}
          </Btn>
        </div>
      </header>

      {justCreated || (view === "all" && !productSet.selectionKeys.length && !selectedCount) ? (
        <Notice tone="info" icon="sparkle" className="mka-intro" title={tt("introTitle")}>
          {tt("introCopy")}
        </Notice>
      ) : null}

      {removedUnknown > 0 ? (
        <Notice
          tone="warning"
          title={plural(tt, "unknownRemovedTitle", removedUnknown)}
          action={
            <Btn variant="ghost" size="sm" onClick={() => setRemovedUnknown(0)}>
              {tt("dismiss")}
            </Btn>
          }
        >
          {tt("unknownRemovedCopy")}
        </Notice>
      ) : null}

      <ReachAttention
        summary={reachSummary}
        tt={tt}
        isIncluding={isIncluding}
        includeDisabled={isDirty}
        onInclude={handleIncludeInCatalog}
        onShow={() => {
          setFilters(EMPTY_FILTERS);
          openSelectedView({ reachOnly: true });
        }}
      />

      <section className="mka-workspace" aria-label={tt("workspaceAria")}>
        <div className="mka-workspace__head">
          <div className="mka-tabs" role="tablist" aria-label={tt("viewLabel")} onKeyDown={handleTabKeyDown}>
            <button
              type="button"
              role="tab"
              id="mka-tab-selected"
              aria-selected={view === "selected"}
              aria-controls="mka-results-panel"
              tabIndex={view === "selected" ? 0 : -1}
              className={cx("mka-tab", view === "selected" && "is-active")}
              onClick={() => openSelectedView()}
            >
              {tt("viewSelected")}
              <span className="mka-count">{selectedCount}</span>
            </button>
            <button
              type="button"
              role="tab"
              id="mka-tab-all"
              aria-selected={view === "all"}
              aria-controls="mka-results-panel"
              tabIndex={view === "all" ? 0 : -1}
              className={cx("mka-tab", view === "all" && "is-active")}
              onClick={() => setView("all")}
            >
              {tt("viewAll")}
            </button>
          </div>
          <span className="mka-selected-live" aria-live="polite">
            {diff.isDirty
              ? [
                  diff.added.length ? plural(tt, "addedCount", diff.added.length) : null,
                  diff.removed.length ? plural(tt, "removedCount", diff.removed.length) : null,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : plural(tt, "selectedCount", selectedCount)}
          </span>
        </div>

        <div id="mka-results-panel" role="tabpanel" aria-labelledby={view === "all" ? "mka-tab-all" : "mka-tab-selected"}>
          <ProductPickerToolbar
            t={t}
            tt={tt}
            filters={filters}
            onFiltersChange={setFilters}
            sort={sort}
            onSortChange={setSort}
            layout={layout}
            onLayoutChange={changeLayout}
            facets={picker.facets}
            resultCount={resultCount}
            searchOnly={view === "selected"}
          />

          {selectedViewReachOnly && view === "selected" ? (
            <div className="mka-chips">
              <button type="button" className="mka-chip" onClick={() => setSelectedViewReachOnly(false)}>
                <span>{tt("filterMeta_not_reaching")}</span>
                <Icon name="x" size={13} />
              </button>
            </div>
          ) : null}

          {visibleProducts.length ? (
            <div className="mka-results-head">
              <BulkCheckbox
                state={bulkState}
                onChange={toggleBulk}
                disabled={!bulkIds.length}
                label={
                  bulkState === "all"
                    ? plural(tt, view === "selected" ? "deselectShown" : "deselectMatching", bulkIds.length)
                    : plural(tt, view === "selected" ? "selectShown" : "selectMatching", bulkIds.length)
                }
              />
              {selectedCount > 0 ? (
                <button
                  type="button"
                  className="pse-link mka-results-head__clear"
                  onClick={() => setSelectedIds(new Set())}
                >
                  {tt("clearSelection")}
                </button>
              ) : null}
            </div>
          ) : null}

          {results}
        </div>
      </section>

      <MetaSetupPanel labelKey={productSet.labelKey} tt={tt} onCopied={() => notify(tt("toastCopied"), "info")} />

      {isDirty ? (
        <div className="pse-savebar mka-savebar" role="region" aria-label={tt("unsavedTitle")}>
          <div className="pse-savebar__text">
            <Icon name="alert" size={18} />
            <div>
              <strong>{tt("unsavedTitle")}</strong>
              <span>
                {[
                  diff.added.length ? plural(tt, "addedCount", diff.added.length) : null,
                  diff.removed.length ? plural(tt, "removedCount", diff.removed.length) : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
          </div>
          <div className="pse-savebar__actions">
            <Btn variant="ghost" onClick={discardChanges} disabled={isSaving}>
              {tt("discard")}
            </Btn>
            <Btn variant="primary" icon="check" onClick={() => save()} disabled={isSaving}>
              {isSaving ? tt("saving") : plural(tt, "saveCount", diff.count)}
            </Btn>
          </div>
        </div>
      ) : null}

      <ProductSetDetailsDrawer
        isOpen={isDetailsOpen}
        mode="edit"
        initialValues={productSet}
        onClose={() => setIsDetailsOpen(false)}
        onSubmit={handleSaveDetails}
        tt={tt}
      />

      <ConfirmDialog
        isOpen={isDeleteOpen}
        tone="danger"
        title={fill(tt("deleteTitle"), { name: productSet.name })}
        confirmLabel={isDeleting ? tt("deleting") : tt("deleteConfirm")}
        cancelLabel={tt("cancel")}
        isBusy={isDeleting}
        onCancel={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
      >
        <p>{tt("deleteCopyProducts")}</p>
        <p className="mka-muted">{tt("deleteCopyMeta")}</p>
        {isDirty ? <p className="mka-muted">{tt("deleteCopyUnsaved")}</p> : null}
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={conflictOpen}
        title={tt("conflictTitle")}
        confirmLabel={isSaving ? tt("saving") : tt("conflictOverwrite")}
        cancelLabel={tt("cancel")}
        isBusy={isSaving}
        onCancel={() => setConflictOpen(false)}
        onConfirm={() => save({ force: true })}
      >
        <p>{tt("conflictCopy")}</p>
        <button type="button" className="pse-link" onClick={reloadTheirs} disabled={isSaving}>
          {tt("conflictReload")}
        </button>
      </ConfirmDialog>
    </div>
  );
};

export default MetaProductSetManage;
